/**
 * TokenService.js - Enhanced service for token management with React 19 compatibility
 *
 * This service manages authentication tokens with improved security,
 * error handling, and compatibility with React 19 patterns.
 *
 * Note: This service does NOT make API calls. It only manages tokens
 * that are already available from other sources (appload, cache, etc.).
 */

import { cacheService } from '../CacheService';

const ACCESS_TOKEN_CACHE_KEY = 'ACCESS_TOKEN';

class TokenService {
  /**
   * Create a new TokenService instance
   *
   * @param {Object} options - Configuration options
   * @param {TokenStorage} options.storage - Storage mechanism for tokens
   */
  constructor(options = {}) {
    this.tokenPromise = null;
  }

  /**
   * Get a valid access token with clear fallback priorities:
   * 1. Primary: Cached access token (updated during refresh)
   * 2. Fallback: Embedded JWT (for initial appload)
   * 3. Fallback: Appload data token (legacy)
   *
   * @returns {Promise<string>} Promise resolving to a valid token
   */
  async getToken() {
    // Primary: Get token from the dedicated access token cache
    const cachedToken = cacheService.get(ACCESS_TOKEN_CACHE_KEY);

    if (cachedToken) {
      return cachedToken;
    }

    // Fallback 1: Embedded JWT (required for initial appload to work)
    if (window.EMBEDDED_JWT) {
      return window.EMBEDDED_JWT;
    }

    // Fallback 2: Appload data token (legacy, should be migrated to primary cache)
    const appData = cacheService.getAppData();
    if (appData?.app_data?.token) {
      // Migrate to primary cache for future use
      this.storeToken(appData.app_data.token);
      return appData.app_data.token;
    }

    return null;
  }

  /**
   * Store a token in the primary access token cache
   * This is the ONLY place where access tokens should be stored/updated
   *
   * @param {string} token - The token to store
   * @param {number} ttlMinutes - Time to live in minutes (default: 1440 = 24 hours)
   */
  storeToken(token, ttlMinutes = 1440) {
    if (token) {
      cacheService.set(ACCESS_TOKEN_CACHE_KEY, token, ttlMinutes);
      // Access token stored in primary cache
    }
  }

  /**
   * Get authentication headers for API requests
   *
   * @returns {Promise<Object>} Promise resolving to headers object
   */
  async getAuthHeaders() {
    const token = await this.getToken();

    if (!token) {
      console.warn('TokenService: No access token available');
      return {};
    }

    return {
      'x-access-token': token,
    };
  }

  /**
   * Clear stored access token
   */
  clearToken() {
    cacheService.invalidate(ACCESS_TOKEN_CACHE_KEY);
  }

  /**
   * Check if an access token exists in the primary cache
   *
   * @returns {boolean} True if token exists, false otherwise
   */
  hasToken() {
    return !!cacheService.get(ACCESS_TOKEN_CACHE_KEY);
  }

  /**
   * Initialize the access token cache with the embedded JWT
   * This should be called once during app initialization
   */
  initializeWithEmbeddedJWT() {
    if (window.EMBEDDED_JWT && !this.hasToken()) {
      this.storeToken(window.EMBEDDED_JWT);
    }
  }
}

export default TokenService;
export const tokenService = new TokenService();
